import { authGuard } from '@auth0/auth0-vue';
import type { RouteRecordRaw } from 'vue-router';

import { APP_URLS, BASIFY, CRUD } from '@/constants';
import {
  anyAdminGuard,
  hardwareTypeGuard,
  manageSimTypeGuard,
  superAdminGuard,
  superOrOrgAdminGuard,
} from '@/router/guards';
import { ENTITY_ROUTES, ROUTE_NAMES, TAB_ROUTES } from '@/router/routes.constants';
import { authenticateUserGuard } from '@/stores/currentUser';

const DashboardRoutes: RouteRecordRaw = {
  path: '/',
  name: ROUTE_NAMES.HOME,
  redirect: '/dashboard',
  meta: {
    requiresAuth: true,
  },
  beforeEnter: [authGuard, authenticateUserGuard],
  component: () => import('@/layouts/DashboardLayout.vue'),
  children: [
    {
      name: ROUTE_NAMES.DASHBOARD,
      path: BASIFY(APP_URLS.dashboard),
      component: () => import('@/views/DashboardPage.vue'),
    },
    {
      path: BASIFY(APP_URLS.contacts),
      children: [
        {
          name: ROUTE_NAMES.CONTACTS,
          path: '',
          component: () => import('@/views/ContactsPage.vue'),
        },
        {
          name: ROUTE_NAMES.CONTACT_CREATE,
          path: CRUD.create,
          component: () => import('@/views/contacts/CreateContact.vue'),
          beforeEnter: [anyAdminGuard],
        },
        {
          name: ROUTE_NAMES.CONTACT_EDIT,
          path: `:id/${CRUD.edit}`,
          component: () => import('@/views/contacts/EditContact.vue'),
          beforeEnter: [anyAdminGuard],
        },
      ],
    },
    {
      path: BASIFY(APP_URLS.divisions),
      beforeEnter: [superOrOrgAdminGuard],
      children: [
        {
          name: ROUTE_NAMES.DIVISIONS,
          path: '',
          component: () => import('@/views/DivisionsPage.vue'),
        },
        {
          name: ROUTE_NAMES.CREATE_DIVISION,
          path: CRUD.create,
          component: () => import('@/views/divisions/CreateDivision.vue'),
        },
        {
          name: ROUTE_NAMES.EDIT_DIVISION,
          path: `:id`,
          component: () => import('@/views/divisions/EditDivision.vue'),
        },
      ],
    },
    {
      name: ROUTE_NAMES.PROFILE,
      path: BASIFY(APP_URLS.profile),
      component: () => import('@/views/ProfilePage.vue'),
    },
    {
      name: ROUTE_NAMES.NOTIFICATION_SETTINGS,
      path: BASIFY(APP_URLS.notifications),
      component: () => import('@/views/NotificationSettingsPage.vue'),
    },
    {
      path: BASIFY(APP_URLS.organizations),
      children: [
        {
          path: '',
          name: ROUTE_NAMES.ORGANIZATIONS,
          component: () => import('@/views/OrganizationsPage.vue'),
        },
        {
          name: ROUTE_NAMES.CREATE_ORGANIZATION,
          path: CRUD.create,
          component: () => import('@/views/organizations/CreateOrganization.vue'),
          beforeEnter: [superAdminGuard],
        },
        {
          name: ROUTE_NAMES.ORGANIZATION,
          path: ':id',
          component: () => import('@/views/OrganizationPage.vue'),
        },
        {
          name: ROUTE_NAMES.EDIT_ORGANIZATION,
          path: `:id/${CRUD.edit}/:tab?`,
          component: () => import('@/views/organizations/EditOrganization.vue'),
        },
      ],
    },
    {
      name: ROUTE_NAMES.ACTIVITY_LOGS,
      path: BASIFY(APP_URLS.activityLogs),
      component: () => import('@/views/ActivityLogsPage.vue'),
      beforeEnter: [superOrOrgAdminGuard],
      children: [
        {
          name: ROUTE_NAMES.ACTIVITY_LOGS_LOCATION,
          path: ':id',
          component: () => import('@/views/ActivityLogsPage.vue'),
        },
      ],
    },
    {
      path: BASIFY(APP_URLS.locations),
      children: [
        {
          path: '',
          name: ROUTE_NAMES.LOCATIONS,
          component: () => import('@/views/LocationsPage.vue'),
        },
        {
          name: ROUTE_NAMES.CREATE_LOCATION,
          path: CRUD.create,
          component: () => import('@/views/locations/CreateLocation.vue'),
          beforeEnter: [superAdminGuard],
        },
        {
          name: ROUTE_NAMES.EDIT_LOCATION,
          path: `/${APP_URLS.organizations}/:orgId/${APP_URLS.locations}/:locId/${CRUD.edit}/:tab?`,
          component: () => import('@/views/locations/EditLocation.vue'),
          beforeEnter: [anyAdminGuard],
        },
        {
          path: `/${APP_URLS.organizations}/:orgId/${APP_URLS.locations}/:locId`,
          component: () => import('@/views/locations/ViewLocation.vue'),
          children: [
            {
              name: ROUTE_NAMES.LOCATION,
              path: '',
              component: () => import('@/views/LocationPage.vue'),
              children: [
                {
                  name: ROUTE_NAMES.LOCATION_GENERAL,
                  path: TAB_ROUTES.GENERAL,
                  component: () => import('@/views/locations/LocationGeneral.vue'),
                },
                {
                  name: ROUTE_NAMES.LOCATION_LINES,
                  path: TAB_ROUTES.SERVICES,
                  component: () => import('@/views/locations/LocationServices.vue'),
                },
                {
                  name: ROUTE_NAMES.LOCATION_DIAGNOSTICS,
                  path: TAB_ROUTES.DIAGNOSTICS,
                  component: () => import('@/views/locations/LocationHardware.vue'),
                },
                {
                  name: ROUTE_NAMES.LOCATION_EMERGENCY,
                  path: TAB_ROUTES.EMERGENCY,
                  component: () => import('@/views/locations/LocationEmergency.vue'),
                  beforeEnter: [superAdminGuard],
                },
                {
                  name: ROUTE_NAMES.LOCATION_PERFORMANCE,
                  path: TAB_ROUTES.PERFORMANCE,
                  component: () => import('@/views/locations/LocationPerformance.vue'),
                },
              ],
            },
            {
              name: ROUTE_NAMES.LOCATION_MEDIA,
              path: 'media',
              component: () => import('@/views/locations/LocationMedia.vue'),
            },
            {
              path: 'lines/ivr',
              component: () => import('@/views/services/IvrPage.vue'),
              children: [
                {
                  name: ROUTE_NAMES.CREATE_IVR,
                  path: CRUD.create,
                  component: () => import('@/views/services/CreateIvr.vue'),
                  beforeEnter: [superAdminGuard],
                },
                {
                  name: ROUTE_NAMES.EDIT_IVR,
                  path: `:id`,
                  component: () => import('@/views/services/EditIvr.vue'),
                  beforeEnter: [superAdminGuard],
                },
              ],
            },
            {
              name: ROUTE_NAMES.CDRS,
              path: `${APP_URLS.cdr}/:number`,
              component: () => import('@/views/CdrsPage.vue'),
            },
            {
              name: ROUTE_NAMES.LOCATION_HARDWARE_CREATE,
              path: `hardware/${CRUD.create}`,
              component: () => import('@/views/hardware/CreateLocationHardware.vue'),
              beforeEnter: [superAdminGuard],
            },
            {
              name: ROUTE_NAMES.LOCATION_HARDWARE_EDIT,
              path: `hardware/:hardware/:id/${CRUD.edit}`,
              component: () => import('@/views/hardware/EditHardware.vue'),
              beforeEnter: [superAdminGuard, hardwareTypeGuard],
            },
            {
              name: ROUTE_NAMES.LOCATION_KIT_EDIT,
              path: `kit/${CRUD.edit}`,
              component: () => import('@/views/hardware/EditKit.vue'),
              beforeEnter: [superAdminGuard],
            },
            {
              name: ROUTE_NAMES.ROUTER_SIMS,
              path: `hardware/:hardware/:id/${ENTITY_ROUTES.SIMS}`,
              component: () => import('@/views/sims/ManageSims.vue'),
              beforeEnter: [superAdminGuard, manageSimTypeGuard],
              children: [
                {
                  name: ROUTE_NAMES.ROUTER_CREATE_SIMS,
                  path: '',
                  alias: CRUD.create,
                  component: () => import('@/views/sims/CreateSims.vue'),
                },
                {
                  name: ROUTE_NAMES.ROUTER_EDIT_SIMS,
                  path: CRUD.edit,
                  component: () => import('@/views/sims/EditSims.vue'),
                },
              ],
            },
            {
              name: ROUTE_NAMES.LINES_EDIT_USER,
              path: `hardware/${ENTITY_ROUTES.GATEWAY}/:id/${ENTITY_ROUTES.SIP_ENDPOINTS}/user/${CRUD.edit}`,
              component: () => import('@/views/services/sip-endpoints/UserEditSipEndpoint.vue'),
            },
            {
              name: ROUTE_NAMES.LINES_EMERYGENCY_ADDRESS,
              path: `${CRUD.edit}/:sipEndpointId/emergency-address`,
              component: () => import('@/views/services/lines/EditLineEmergencyAddress.vue'),
            },
          ],
        },
      ],
    },
    {
      name: ROUTE_NAMES.LINES,
      path: `/${APP_URLS.lines}`,
      component: () => import('@/views/LinesPage.vue'),
    },
    {
      path: BASIFY(APP_URLS.users),
      children: [
        {
          name: ROUTE_NAMES.USERS,
          path: '',
          component: () => import('@/views/UsersPage.vue'),
        },
        {
          name: ROUTE_NAMES.USER_CREATE,
          path: CRUD.create,
          component: () => import('@/views/users/InviteUsers.vue'),
          beforeEnter: [anyAdminGuard],
        },
        {
          name: ROUTE_NAMES.USER_EDIT,
          path: `:id/${CRUD.edit}`,
          component: () => import('@/views/users/EditUser.vue'),
          beforeEnter: [anyAdminGuard],
        },
      ],
    },
  ],
};

export default DashboardRoutes;
